// AuthService - Handles all authentication-related API calls
import ApiService, { ApiResponse } from './ApiService'
import { UserRole } from '@/constants/roles.constant'
import type { SignInCredential } from '@/@types/auth'

export interface School {
  id: string
  name: string
  lat: number
  lon: number
  checkinRadiusM: number
  email?: string
  phone?: string
  address?: string
  timezone?: string
  type?: string
  curriculum?: string
  isActive?: boolean
  activeTerm?: ActiveTerm
}

export interface ActiveTerm {
  id: string
  name: string
  isPaid: boolean
  startDate: string
  endDate: string
  academicYear?: string
  termNumber?: number
}

export interface AuthUser {
  id: string
  email: string
  role: UserRole
  firstName: string
  lastName: string
  phone?: string
  schoolId: string
  school?: School
  permissions?: string[]
  photoUrl?: string
  dateOfBirth?: string
  gender?: string
  address?: string
}

export interface SignInResponse {
  accessToken: string
  refreshToken: string
  user: AuthUser
  school: School
  activeTerm: ActiveTerm
}

export interface RefreshTokenResponse {
  accessToken: string
  refreshToken: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
  confirmPassword: string
}

class AuthService {
  async signIn(credentials: SignInCredential): Promise<ApiResponse<SignInResponse>> {
    return ApiService.post<SignInResponse, SignInCredential>('/auth/login', credentials)
  }

  async signOut(): Promise<ApiResponse<void>> {
    return ApiService.post<void>('/auth/logout')
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<RefreshTokenResponse>> {
    return ApiService.post<RefreshTokenResponse, { refreshToken: string }>('/auth/refresh', {
      refreshToken,
    })
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }, ForgotPasswordRequest>('/auth/forgot-password', {
      email,
    })
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }, ResetPasswordRequest>('/auth/reset-password', data)
  }

  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }, { token: string }>('/auth/verify-email', { token })
  }

  async getCurrentUser(): Promise<ApiResponse<AuthUser>> {
    return ApiService.get<AuthUser>('/auth/profile')
  }

  async updateProfile(data: Partial<AuthUser>): Promise<ApiResponse<AuthUser>> {
    return ApiService.patch<AuthUser, Partial<AuthUser>>('/auth/profile', data)
  }

  async changePassword(data: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }, typeof data>('/auth/change-password', data)
  }
}

const authServiceInstance = new AuthService()
export default authServiceInstance
